import { Box, Divider, Stack, Typography, styled } from "@mui/material"
import { MarkdownDisplay, PaperContainer } from "@pharmupp/p3-front-commons"
import type { Except } from "type-fest"
import { FlashChip } from "../../../News"
import {
  type ApiDisplayNews,
  type NewsCategory,
  PostType,
} from "../../../News/model"
import { DisplayNumberViews } from "../../../core"
import { EventSection } from "./EventSection"
import { NewsCategoryChip } from "./NewsCategoryChip"
import { NewsCreatorRole } from "./NewsCreatorRole"

export type FormattedNews<T extends ApiDisplayNews = ApiDisplayNews> = Except<
  T,
  "rubric" | "image" | "logo" | "publicationDate"
> & {
  rubric: string
  publicationDate: string
  image?: string
  logo?: string
}

export interface NewsProps {
  news: FormattedNews | undefined
  newsLoading: boolean
  subscribe?: () => Promise<void> | void
  subscribeLoading?: boolean
  unsubscribe?: () => Promise<void> | void
  unsubscribeLoading?: boolean
  category?: NewsCategory
  isSponsored?: boolean
}

export const News = ({
  news,
  newsLoading,
  isSponsored = false,
  ...props
}: NewsProps) => {
  return (
    <PaperContainer
      loading={newsLoading}
      component={Box}
      width="100%"
      px={21}
      py={4}
      mb={19}
      sx={{ position: "relative", overflow: "visible" }}
    >
      {isSponsored && <SponsorChip>Sponsorisé</SponsorChip>}
      {news && <NewsInner news={news} {...props} />}
    </PaperContainer>
  )
}

const NewsInner = ({
  news,
  category,
  ...props
}: Omit<NewsProps, "newsLoading" | "news"> & { news: FormattedNews }) => {
  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={5}
      >
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {!!news.flash && <FlashChip />}

            <NewsCategoryChip news={news} category={category} />
          </Stack>

          {/* Lab logo */}
          {!!news.logo && (
            <img src={news.logo} width="167px" alt="Logo du laboratoire" />
          )}
        </Stack>

        <Stack direction="row" spacing={3}>
          {!!news.views && <DisplayNumberViews views={news.views} variant="full" />}
        </Stack>
      </Stack>

      {/* TITLE */}
      <Box mb={5}>
        <Typography
          variant="h1"
          fontSize="36px"
          fontWeight={600}
          color="common.darkBlue"
        >
          {news.title}
        </Typography>
      </Box>

      <Divider />

      {/* POST META */}
      <Box my={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2}>
            <Typography color="grey.light">
              Publié le {news.publicationDate}
            </Typography>
            <Typography color="common.darkBlue">
              Auteur - {news.author}
              <NewsCreatorRole {...news} />
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <Divider />

      {/* IMAGE */}
      {!!news.image && (
        <Box mt={5}>
          <StyledImg src={news.image} alt="Illustration" />
        </Box>
      )}

      {/* CONTENT */}
      <Box px={14} my={5}>
        <Box fontSize="0.875rem" lineHeight="20px" color="common.darkBlue" mb={5}>
          <MarkdownDisplay>{news.description}</MarkdownDisplay>
        </Box>
      </Box>

      {/* EVENT DETAILS */}
      {news.postType === PostType.EVENT && <EventSection news={news} {...props} />}
    </>
  )
}

const StyledImg = styled("img")({
  width: "100%",
  objectFit: "cover",
  aspectRatio: "2.35 / 1",
})

const SponsorChip = styled(Typography)(
  ({ theme: { palette, spacing, typography } }) => ({
    position: "absolute",
    top: typography.pxToRem(-18 / 2),
    left: spacing(21),
    borderRadius: "3px",
    lineHeight: typography.pxToRem(18),
    fontWeight: 600,
    fontSize: "0.75rem",
    color: palette.grey.dark,
  }),
)
